// FRONT-1698: the thinking slider — six stops (Fast, Medium, High, Very high,
// Pro, Auto) on a heat-coloured track. One component for the chat box, the
// phone composer and Settings → Models, so all three look and behave alike.
// It is a plain, hand-built slider rather than the Radix one because the phone
// composer must keep the keyboard focused in its text box while the slider is
// used (`keepFocus`), and Radix moves focus to its thumb on every press.

import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';
import type { ThinkingLevel } from '~/lib/types/llm';
import {
  AUTO_STOP,
  HIGH_STOP,
  along,
  THINKING_STOPS,
  TOP_TIER_STOPS,
  levelToSave,
  stopAtPointer,
  stopForKey,
  stopIndexOf,
  trackFill,
} from '~/lib/thinking-stops';

// Auto's thumb carries the whole ramp: the model picks where on it to land.
const AUTO_THUMB =
  'conic-gradient(var(--thinking-1), var(--thinking-2), var(--thinking-3), var(--thinking-4), var(--thinking-5), var(--thinking-1))';

/**
 * How long the compact slider's level name stays after a choice before it
 * fades. The composer row is quieter without a permanent word beside the
 * track; the colour carries the level, the name confirms a change.
 */
export const LABEL_LINGER_MS = 2000;

// The empty bar and its stop marks. Both derive from the secondary ink so they
// follow the theme: the bar is a faint tint of it, a mark a clearer one. A mark
// on the coloured fill is a soft wash of the page colour, never a hard hole.
const TRACK = 'color-mix(in srgb, var(--muted-foreground) 16%, transparent)';
const DOT_ON_TRACK = 'color-mix(in srgb, var(--muted-foreground) 60%, transparent)';
const DOT_ON_FILL = 'color-mix(in srgb, var(--background) 55%, transparent)';


export interface ThinkingSliderProps {
  /** The stored level; null/undefined is Auto. */
  value: ThinkingLevel | null | undefined;
  /** Called once per committed move (a click, the end of a drag, a key press). */
  onChange: (level: ThinkingLevel | null) => void;
  /** `compact` sits in a composer row; `full` is the Settings control with a label under every stop. */
  variant?: 'compact' | 'full';
  /** Why the slider cannot be used right now; null when it can. Disabled iff set. */
  disabledReason?: string | null;
  ariaLabel?: string;
  /** Never take focus on press — the phone composer's text box must keep it. */
  keepFocus?: boolean;
  /**
   * The model in use has no Very high / Pro of its own and thinks at High
   * there. Those two stops stay choosable (the level is the person's, and
   * applies on models that have them) but are drawn muted, and the hint says so.
   */
  cappedAtHigh?: boolean;
  testId?: string;
  className?: string;
}

export function ThinkingSlider({
  value,
  onChange,
  variant = 'compact',
  disabledReason = null,
  ariaLabel = 'Thinking',
  keepFocus = false,
  cappedAtHigh = false,
  testId = 'thinking-slider',
  className,
}: ThinkingSliderProps) {
  // The drawn bar, not the padded hit area around it: marks are placed on it.
  const barRef = useRef<HTMLDivElement>(null);
  const reasonId = useId();
  const [dragStop, setDragStop] = useState<number | null>(null);
  const [recentlyChosen, setRecentlyChosen] = useState(false);
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(lingerTimer.current), []);
  const disabled = disabledReason !== null;
  const committed = stopIndexOf(value);
  const index = dragStop ?? committed;
  const stop = THINKING_STOPS[index]!;
  const compact = variant === 'compact';

  const commit = (next: number) => {
    // Show the name for every choice, even one that lands where it was.
    setRecentlyChosen(true);
    clearTimeout(lingerTimer.current);
    lingerTimer.current = setTimeout(() => setRecentlyChosen(false), LABEL_LINGER_MS);
    const level = levelToSave(next, value);
    if (level !== undefined) onChange(level);
  };
  const stopFromPointer = (e: PointerEvent) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return committed;
    return stopAtPointer(e.clientX, rect.left, rect.width);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    // Cancelling pointerdown also cancels the mousedown that would move focus.
    if (keepFocus) e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragStop(stopFromPointer(e));
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStop === null) return;
    setDragStop(stopFromPointer(e));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStop === null) return;
    const next = stopFromPointer(e);
    setDragStop(null);
    // Became unavailable mid-drag (an answer started): drop the move.
    if (!disabled) commit(next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const next = stopForKey(e.key, committed);
    if (next === null) return;
    e.preventDefault();
    commit(next);
  };

  const capped = cappedAtHigh && TOP_TIER_STOPS.has(index);
  // The compact name shows while dragging and for LABEL_LINGER_MS after a
  // choice, then fades. Not on hover or focus: the pointer usually rests on
  // the slider after a click, which would keep the name up indefinitely. The
  // hover hint and the screen-reader value carry the name the rest of the time.
  const labelShown = dragStop !== null || recentlyChosen;
  const hint =
    disabledReason ??
    (capped
      ? `Thinking: ${stop.label}. This model's deepest level is High, so it thinks at High. ${stop.label} applies on Claude models.`
      : `Thinking: ${stop.label}. ${stop.hint}`);

  const slider = (
    <div
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={AUTO_STOP}
      aria-valuenow={index}
      aria-valuetext={stop.label}
      aria-disabled={disabled || undefined}
      aria-describedby={disabled ? reasonId : undefined}
      data-testid={testId}
      data-level={stop.level ?? 'auto'}
      data-capped={capped || undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'group flex select-none items-center gap-2 rounded-full outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring/50',
        disabled && 'opacity-50',
        compact ? 'h-7' : 'w-full max-w-md flex-col items-stretch gap-1.5',
        className,
      )}
    >
      <div
        data-testid={`${testId}-track`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDragStop(null)}
        className={cn(
          'relative flex touch-none items-center',
          compact ? 'h-7 w-24 px-1.5' : 'h-8 w-full px-2',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <div ref={barRef} className="relative h-1.5 w-full rounded-full" style={{ background: TRACK }}>
          <div
            data-testid={`${testId}-fill`}
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 motion-reduce:transition-none"
            style={{ width: along(index / AUTO_STOP), background: trackFill(index) }}
          />
          {/* Small and soft: a stop is a mark on the bar, not a second control —
              the chosen one is the thumb, several times its size. Drawn as SVG
              circles so a mark at a fractional pixel stays round (a 4px CSS
              box snaps to 4 or 5px and reads as an oval). The box is inset like
              `along`, so a circle's cx is the same fraction. */}
          <svg aria-hidden="true" className="absolute inset-y-0 left-[3px] h-full w-[calc(100%-6px)] overflow-visible">
            {THINKING_STOPS.map((s, i) => (
              <circle
                key={s.label}
                data-testid={`${testId}-dot`}
                cx={`${(i / AUTO_STOP) * 100}%`}
                cy="50%"
                r={2}
                fill={i <= index ? DOT_ON_FILL : DOT_ON_TRACK}
                opacity={i === index ? 0 : cappedAtHigh && TOP_TIER_STOPS.has(i) ? 0.45 : 1}
              />
            ))}
          </svg>
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background',
              'transition-[left] duration-150 motion-reduce:transition-none',
              compact ? 'size-3.5' : 'size-4',
              !disabled && 'group-hover:scale-110',
            )}
            style={{
              left: along(index / AUTO_STOP),
              background: index === AUTO_STOP ? AUTO_THUMB : stop.color,
              boxShadow: `0 0 0 3px color-mix(in srgb, ${stop.color} 28%, transparent)`,
            }}
          />
        </div>
      </div>
      {compact ? (
        // Fixed width, so a longer level name never moves what sits beside it,
        // and the slot stays reserved while the name is faded out.
        <span
          aria-hidden="true"
          className={cn(
            'w-[4.5rem] shrink-0 truncate text-xs font-medium transition-opacity motion-reduce:transition-none',
            labelShown ? 'opacity-100 duration-150 ease-out' : 'opacity-0 duration-700 ease-in-out',
            capped && 'underline decoration-dotted underline-offset-2',
          )}
          style={{ color: capped ? THINKING_STOPS[HIGH_STOP]!.color : stop.color }}
          data-testid={`${testId}-label`}
          data-shown={labelShown || undefined}
        >
          {stop.label}
        </span>
      ) : (
        <div className="relative h-4 w-full px-2 text-[11px]" aria-hidden="true">
          {THINKING_STOPS.map((s, i) => (
            <span
              key={s.label}
              className={cn(
                'absolute -translate-x-1/2 whitespace-nowrap',
                i === index ? 'font-semibold' : 'text-muted-foreground',
              )}
              style={{
                left: `calc(0.5rem + (100% - 1rem) * ${i / AUTO_STOP})`,
                ...(i === index ? { color: s.color } : {}),
              }}
            >
              {s.label}
            </span>
          ))}
        </div>
      )}
      {disabled && (
        <span id={reasonId} className="sr-only" data-testid={`${testId}-reason`}>
          {disabledReason}
        </span>
      )}
    </div>
  );

  return (
    <Tip label={hint}>
      <span className={cn('inline-flex', !compact && 'w-full')}>{slider}</span>
    </Tip>
  );
}
